<script setup>
import { ref, watch, onMounted, onBeforeUnmount } from 'vue';
import {
  MODE_DRAWS,
  resolvePreset,
  STATE_LABELS,
  detectDarkFromDom,
  prefersDarkMedia,
} from './engine';

// Orb de IA (SITEUP V2). EXCLUSIVO para estados de IA — humano digitando
// continua com typing dots, loading de sistema continua com svg-spinners.
// Estados: working=gerando resposta · searching=RAG · solving=ferramenta ·
// listening=transcrevendo áudio · connecting=integração.
// `size` só aceita os dois presets tunados do motor: 20 (inline) e 64 (avatar).
const props = defineProps({
  state: { type: String, default: 'working' },
  size: { type: Number, default: 20, validator: v => v === 20 || v === 64 },
  label: { type: String, default: '' },
});

const canvasRef = ref(null);
let rafId = null;
let observer = null;
let inViewport = true;

const reducedMotion = () =>
  typeof matchMedia !== 'undefined' &&
  matchMedia('(prefers-reduced-motion: reduce)').matches;

const drawFrame = t => {
  const canvas = canvasRef.value;
  if (!canvas) return;
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  const px = Math.round(props.size * dpr);
  if (canvas.width !== px) {
    canvas.width = px;
    canvas.height = px;
  }
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, props.size, props.size);
  const dark = detectDarkFromDom(canvas) ?? prefersDarkMedia();
  const { mode, speed, opts } = resolvePreset(props.state, props.size);
  MODE_DRAWS[mode](ctx, props.size, t * speed, dark, opts);
};

const stop = () => {
  if (rafId) cancelAnimationFrame(rafId);
  rafId = null;
};

const start = () => {
  if (rafId || !inViewport || document.visibilityState === 'hidden') return;
  if (reducedMotion()) {
    drawFrame(0.6); // frame estático, mesma pose do original
    return;
  }
  const loop = () => {
    drawFrame(performance.now() / 1000);
    rafId = requestAnimationFrame(loop);
  };
  rafId = requestAnimationFrame(loop);
};

const onVisibility = () => {
  if (document.visibilityState === 'hidden') stop();
  else start();
};

onMounted(() => {
  drawFrame(reducedMotion() ? 0.6 : performance.now() / 1000);
  if (typeof IntersectionObserver !== 'undefined') {
    observer = new IntersectionObserver(([entry]) => {
      inViewport = entry.isIntersecting;
      if (inViewport) start();
      else stop();
    });
    observer.observe(canvasRef.value);
  }
  document.addEventListener('visibilitychange', onVisibility);
  start();
});

onBeforeUnmount(() => {
  stop();
  observer?.disconnect();
  document.removeEventListener('visibilitychange', onVisibility);
});

watch(
  () => [props.state, props.size],
  () => {
    if (reducedMotion() || !rafId) drawFrame(0.6);
  }
);
</script>

<template>
  <canvas
    ref="canvasRef"
    role="img"
    class="block"
    :aria-label="label || STATE_LABELS[state]"
    :style="{ width: `${size}px`, height: `${size}px` }"
  />
</template>
